/**
 * Supplemental Physician Order (SPO) — static options + empty form state.
 * Mirrors the SPO paper/digital layout (sections 1–12).
 */

export const PRIORITY_LEVELS = ["Routine", "Urgent", "STAT"];

export const ORDER_CATEGORIES = [
  "New Service",
  "Med Change",
  "Lab Order",
  "Wound Care",
  "Discharge",
  "Other",
];

export const LAB_ORDERS = ["CBC", "CMP", "BMP", "PT/INR", "UA/Cx", "Other"];

export const RESULTS_TO_OPTIONS = ["Physician", "Agency", "Both"];

export const DME_OPTIONS = [
  "Walker",
  "Wheelchair",
  "Hospital Bed",
  "Commode",
  "O2 Concentrator",
];

export const EDUCATION_OPTIONS = [
  "Med Safety",
  "Fall Prev",
  "Disease Mgmt",
  "Equipment Use",
];

export const METHOD_RECEIVED_OPTIONS = [
  "Telephone",
  "Verbal (In Person)",
  "Fax",
  "Secure Email",
  "EMR Portal",
];

export const DISCIPLINE_OPTIONS = [
  { key: "rn", label: "RN", hint: "Skilled Nurse" },
  { key: "pt", label: "PT", hint: "Physical Therapy" },
  { key: "ot", label: "OT", hint: "Occupational Therapy" },
  { key: "st", label: "ST", hint: "Speech Therapy" },
  { key: "msw", label: "MSW", hint: "Medical Social Work" },
];

export const OFFICE_DISCIPLINES = ["RN", "PT", "OT", "ST", "HHA", "Billing"];

export const EMR_CHECKLIST = [
  {
    key: "identityVerification",
    title: "Identity Verification",
    description: "Signature matches authorized provider database.",
  },
  {
    key: "timelyFiling",
    title: "Timely Filing Rule",
    description: "Order received within 48 hours of verbal request.",
  },
  {
    key: "clinicalConsistency",
    title: "Clinical Consistency",
    description: "Orders align with existing Care Plan goals.",
  },
  {
    key: "documentationLinked",
    title: "Documentation Linked",
    description: "SPO scanned and linked to patient encounter.",
  },
];

export const MED_CHANGE_FLAGS = ["New Med", "Changed Dose", "Discontinued"];

export const newMedicationRowId = () =>
  `med-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

export const emptyMedicationRow = () => ({
  id: newMedicationRowId(),
  medicationName: "",
  dose: "",
  route: "",
  frequency: "",
  startDate: "",
});

const today = () => new Date().toISOString().slice(0, 10);

/** Full editable SPO payload (persisted as JSON on the dedicated row). */
export const emptySpoPayload = () => ({
  // 1. Patient Information
  patientNameDisplay: "",
  mrn: "",
  dateOfBirth: "",
  socDate: "",
  certPeriodFrom: "",
  certPeriodTo: "",
  primaryDiagnoses: "",
  orderingPhysician: "",
  physicianNpi: "",

  // 2. Supplemental Order Information
  orderNumber: "",
  orderDate: today(),
  effectiveDate: "",
  priorityLevel: "Routine",
  orderCategories: [],

  // 3. Physician Order (required)
  orderDetailsJustification: "",

  // 4. Medication Orders
  medications: [],
  medicationChanges: [],
  medicationComments: "",

  // 5. Skilled Service Orders
  skilledDisciplines: [],
  skilledFrequency: "",
  skilledDuration: "",
  skilledSpecialInstructions: "",

  // 6. Wound Care Orders
  woundLocation: "",
  woundTreatment: "",
  woundDressingType: "",
  woundFrequency: "",
  woundSpecialInstructions: "",

  // 7. Laboratory / Diagnostic Orders
  labOrders: [],
  labCollectionDate: "",
  labResultsTo: "Both",

  // 8. DME / Equipment Orders
  dmeEquipment: [],
  dmeInstructions: "",

  // 9. Patient / Caregiver Notification
  patientInformed: "",
  educationProvided: [],
  notificationComments: "",

  // 10. Agency Verification
  receivedByClinician: "",
  receivedByTitle: "",
  dateReceived: "",
  timeReceived: "",
  methodReceived: [],

  // 11. Physician Authentication (display/meta; clinical e-sign is separate)
  physicianAuthName: "",
  physicianAuthDate: "",
  physicianAuthTime: "",

  // 12. Agency Processing (office use)
  processedBy: "",
  processedDate: "",
  enteredIntoEmr: "",
  qaReviewed: "",
  disciplinesNotified: [],
  emrChecklist: {
    identityVerification: false,
    timelyFiling: false,
    clinicalConsistency: false,
    documentationLinked: false,
  },
});

export const toggleInList = (list, item) => {
  const arr = Array.isArray(list) ? list : [];
  return arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];
};
